// Acesso ao Firestore do modulo de controle financeiro.
//
// Estrutura:
//   financeiro/config            -> taxas, listas e metas (doc unico)
//   financeiro/comissoes         -> % por servico e meta de cada barbeiro (doc unico)
//   financeiro/receitas/itens/*  -> um documento por lancamento de receita
//   financeiro/despesas/itens/*  -> um documento por despesa
//
// As regras do firestore.rules cobrem a arvore inteira de /financeiro.

import { db, refAgenda } from '@/firebaseConfig'
import {
    CONFIG_PADRAO,
    COMISSAO_PADRAO,
    chaveMes,
    primeiroDiaDoMes,
    ultimoDiaDoMes,
    sugerirCategoria,
    montarResumoArmazenavel,
    resumoMensal,
    numero,
    CATEGORIA_TAXA_CREDITO
} from './financeiroCalculos'

const refFinanceiro = db.collection('financeiro')
const refConfig = refFinanceiro.doc('config')
const refComissoes = refFinanceiro.doc('comissoes')
const refReceitas = refFinanceiro.doc('receitas').collection('itens')
const refDespesas = refFinanceiro.doc('despesas').collection('itens')
const refResumos = refFinanceiro.doc('resumos').collection('meses')

function assinatura(usuario){
    const perfil = usuario || {}
    return {
        atualizadoEm: new Date().toISOString(),
        atualizadoPor: perfil.email || perfil.name || 'desconhecido',
        atualizadoPorNome: perfil.name || perfil.email || 'desconhecido'
    }
}

export async function carregarConfig(){
    const snap = await refConfig.get()
    if(!snap.exists){
        return { ...CONFIG_PADRAO }
    }

    // Mescla com o padrao para que campos novos nao quebrem docs antigos.
    const dados = snap.data()
    const categoriasDespesa = Array.isArray(dados.categoriasDespesa)
        ? dados.categoriasDespesa
        : CONFIG_PADRAO.categoriasDespesa

    return {
        ...CONFIG_PADRAO,
        ...dados,
        // ConfiguraÃ§Ãµes antigas podem ter uma lista salva sem a categoria
        // especial da taxa manual de crÃ©dito.
        categoriasDespesa: Array.from(new Set([
            ...categoriasDespesa,
            CATEGORIA_TAXA_CREDITO
        ]))
    }
}

export async function salvarConfig(config, usuario){
    const dados = { ...config, ...assinatura(usuario) }
    await refConfig.set(dados, { merge: true })
    return dados
}

export async function carregarComissoes(barbeiros){
    const snap = await refComissoes.get()
    const salvas = snap.exists && snap.data().barbeiros ? snap.data().barbeiros : {}
    const comissoes = {}

    // Todo barbeiro cadastrado aparece, mesmo sem percentual salvo ainda.
    ;(barbeiros || []).forEach((barbeiro) => {
        comissoes[barbeiro.id] = {
            ...COMISSAO_PADRAO,
            ...(salvas[barbeiro.id] || {}),
            nome: barbeiro.nome
        }
    })

    // Preserva percentuais de barbeiros que sairam do cadastro, para nao
    // recalcular o historico com valores diferentes dos que foram acordados.
    Object.keys(salvas).forEach((id) => {
        if(!comissoes[id]){
            comissoes[id] = { ...COMISSAO_PADRAO, ...salvas[id], inativo: true }
        }
    })

    return comissoes
}

export async function salvarComissoes(comissoes, usuario){
    await refComissoes.set({ barbeiros: comissoes, ...assinatura(usuario) }, { merge: true })
    return comissoes
}

function documentoParaItem(doc){
    return { ...doc.data(), id: doc.id }
}

export async function listarReceitas(mes){
    const snap = await refReceitas.where('mes', '==', mes).get()
    return snap.docs.map(documentoParaItem).sort((a, b) => (a.data || '') > (b.data || '') ? 1 : -1)
}

export async function listarDespesas(mes){
    const snap = await refDespesas.where('mes', '==', mes).get()
    return snap.docs.map(documentoParaItem).sort((a, b) => (a.data || '') > (b.data || '') ? 1 : -1)
}

export async function salvarReceita(receita, usuario){
    const dados = {
        data: receita.data || '',
        mes: chaveMes(receita.data),
        agendaId: receita.agendaId || null,
        cliente: receita.cliente || '',
        barbeiroId: receita.barbeiroId || '',
        barbeiroNome: receita.barbeiroNome || '',
        servico: receita.servico || '',
        categoria: receita.categoria || 'Outro',
        pagamento: receita.pagamento || '',
        valorBruto: numero(receita.valorBruto),
        desconto: numero(receita.desconto),
        status: receita.status || 'Recebido',
        observacao: receita.observacao || '',
        origem: receita.origem || 'manual',
        ...assinatura(usuario)
    }

    if(receita.id){
        await refReceitas.doc(receita.id).set(dados, { merge: true })
        return { ...dados, id: receita.id }
    }

    const criado = await refReceitas.add({ ...dados, criadoEm: new Date().toISOString() })
    return { ...dados, id: criado.id }
}

export async function salvarDespesa(despesa, usuario){
    const dados = {
        data: despesa.data || '',
        mes: chaveMes(despesa.data),
        descricao: despesa.descricao || '',
        categoria: despesa.categoria || '',
        tipo: despesa.tipo || 'Fixa',
        apropriacao: despesa.apropriacao || 'Indireta',
        fornecedor: despesa.fornecedor || '',
        pagamento: despesa.pagamento || '',
        valor: numero(despesa.valor),
        status: despesa.status || 'Pago',
        vencimento: despesa.vencimento || '',
        ...assinatura(usuario)
    }

    if(despesa.id){
        await refDespesas.doc(despesa.id).set(dados, { merge: true })
        return { ...dados, id: despesa.id }
    }

    const criado = await refDespesas.add({ ...dados, criadoEm: new Date().toISOString() })
    return { ...dados, id: criado.id }
}

export async function removerReceita(id){
    await refReceitas.doc(id).delete()
}

export async function removerDespesa(id){
    await refDespesas.doc(id).delete()
}

export async function salvarReceitasEmLote(receitas, usuario){
    const lotes = []
    let lote = db.batch()
    let operacoes = 0

    receitas.forEach((receita) => {
        const dados = {
            ...receita,
            // A data pode ter sido editada na tabela: o mes precisa acompanhar,
            // senao o lancamento fica preso no mes em que foi criado.
            mes: chaveMes(receita.data),
            valorBruto: numero(receita.valorBruto),
            desconto: numero(receita.desconto),
            ...assinatura(usuario)
        }
        delete dados.id
        lote.set(refReceitas.doc(receita.id), dados, { merge: true })
        operacoes += 1

        // O Firestore aceita no maximo 500 operacoes por lote.
        if(operacoes === 450){
            lotes.push(lote)
            lote = db.batch()
            operacoes = 0
        }
    })

    if(operacoes > 0){
        lotes.push(lote)
    }

    await Promise.all(lotes.map((item) => item.commit()))
}

// Reconcilia o valor bruto dos lancamentos com o preco que esta na agenda.
// Serve para corrigir importacoes antigas sem apagar nada: forma de pagamento,
// desconto, status e categoria ja preenchidos sao preservados.
export async function recalcularValoresDaAgenda(mes, usuario){
    const inicio = new Date(`${primeiroDiaDoMes(mes)}T00:00:00`).getTime()
    const fim = new Date(`${ultimoDiaDoMes(mes)}T23:59:59`).getTime()

    const [agendaSnap, receitas] = await Promise.all([
        refAgenda.where('dataQuery', '>=', inicio).where('dataQuery', '<=', fim).get(),
        listarReceitas(mes)
    ])

    const precoPorAgendaId = {}
    agendaSnap.docs.forEach((doc) => {
        const agenda = { ...doc.data(), docId: doc.id }
        if(agenda.servico){
            precoPorAgendaId[agenda.id || agenda.docId] = numero(agenda.servico.preco)
        }
    })

    const divergentes = receitas.filter((receita) => {
        if(!receita.agendaId || precoPorAgendaId[receita.agendaId] === undefined){
            return false
        }
        return numero(receita.valorBruto) !== precoPorAgendaId[receita.agendaId]
    })

    if(!divergentes.length){
        return { corrigidos: 0, diferenca: 0 }
    }

    const diferenca = divergentes.reduce((acc, receita) => {
        return acc + (precoPorAgendaId[receita.agendaId] - numero(receita.valorBruto))
    }, 0)

    const lotes = []
    let lote = db.batch()
    let operacoes = 0

    divergentes.forEach((receita) => {
        lote.update(refReceitas.doc(receita.id), {
            valorBruto: precoPorAgendaId[receita.agendaId],
            ...assinatura(usuario)
        })
        operacoes += 1

        if(operacoes === 450){
            lotes.push(lote)
            lote = db.batch()
            operacoes = 0
        }
    })

    if(operacoes > 0){
        lotes.push(lote)
    }

    await Promise.all(lotes.map((item) => item.commit()))

    return { corrigidos: divergentes.length, diferenca }
}

// ---------------------------------------------------------------------------
// Consolidado mensal
//
// Os documentos em financeiro/resumos/meses sao CACHE, nunca a fonte da verdade:
// existem para a visao de periodo nao precisar ler milhares de lancamentos.
// Podem ser reconstruidos a qualquer momento a partir dos lancamentos, e a
// sincronizacao abaixo regrava sozinha sempre que os numeros divergem.
// ---------------------------------------------------------------------------

export async function listarResumosMensais(mesInicial, mesFinal){
    const snap = await refResumos
        .where('mes', '>=', mesInicial)
        .where('mes', '<=', mesFinal)
        .get()

    return snap.docs
        .map((doc) => ({ ...doc.data(), mes: doc.id }))
        .sort((a, b) => a.mes > b.mes ? 1 : -1)
}

export async function salvarResumoMensal(mes, resumo, usuario){
    const dados = { ...montarResumoArmazenavel(mes, resumo), ...assinatura(usuario) }
    await refResumos.doc(mes).set(dados)
    return dados
}

// Regrava o cache apenas quando os numeros mudaram de fato, para nao gastar
// escrita a cada abertura de tela.
export async function sincronizarResumoMensal(mes, resumo, usuario){
    const snap = await refResumos.doc(mes).get()
    const novo = montarResumoArmazenavel(mes, resumo)

    if(snap.exists){
        const atual = montarResumoArmazenavel(mes, snap.data())
        if(JSON.stringify(atual) === JSON.stringify(novo)){
            return { atualizado: false }
        }
    }

    await refResumos.doc(mes).set({ ...novo, ...assinatura(usuario) })
    return { atualizado: true }
}

// Reconstroi o cache de varios meses lendo os lancamentos de verdade.
// E a operacao cara — roda so quando o usuario pede.
export async function recalcularResumosDoPeriodo(meses, barbeiros, usuario, aoProgredir){
    const config = await carregarConfig()
    const comissoes = await carregarComissoes(barbeiros)
    let atualizados = 0

    for(let i = 0; i < meses.length; i++){
        const mes = meses[i]
        const [receitas, despesas] = await Promise.all([
            listarReceitas(mes),
            listarDespesas(mes)
        ])

        const resumo = resumoMensal(receitas, despesas, config, comissoes, barbeiros)
        const resultado = await sincronizarResumoMensal(mes, resumo, usuario)
        if(resultado.atualizado){
            atualizados += 1
        }

        if(aoProgredir){
            aoProgredir(i + 1, meses.length)
        }
    }

    return { meses: meses.length, atualizados }
}

function dataDoAgendamento(agenda){
    if(agenda.start){
        return String(agenda.start).substring(0, 10)
    }
    if(agenda.dataQuery){
        const data = new Date(agenda.dataQuery)
        const mes = `${data.getMonth() + 1}`.padStart(2, '0')
        const dia = `${data.getDate()}`.padStart(2, '0')
        return `${data.getFullYear()}-${mes}-${dia}`
    }
    return ''
}

// Traz da agenda os atendimentos do mes que ainda nao viraram lancamento.
// O vinculo e feito por agendaId, entao rodar a importacao duas vezes nao duplica.
export async function importarDaAgenda(mes, config, statusPadrao){
    const inicio = new Date(`${primeiroDiaDoMes(mes)}T00:00:00`).getTime()
    const fim = new Date(`${ultimoDiaDoMes(mes)}T23:59:59`).getTime()

    const [agendaSnap, receitasExistentes] = await Promise.all([
        refAgenda.where('dataQuery', '>=', inicio).where('dataQuery', '<=', fim).get(),
        listarReceitas(mes)
    ])

    const jaImportados = new Set(
        receitasExistentes.filter((item) => item.agendaId).map((item) => item.agendaId)
    )
    const mapaCategorias = (config || {}).mapaCategorias || {}

    const novos = agendaSnap.docs
        .map((doc) => ({ ...doc.data(), docId: doc.id }))
        .filter((agenda) => agenda.servico && !jaImportados.has(agenda.id || agenda.docId))
        .map((agenda) => {
            const servico = agenda.servico || {}
            const barbeiro = agenda.barbeiro || {}
            const cliente = agenda.cliente || {}

            return {
                data: dataDoAgendamento(agenda),
                mes,
                agendaId: agenda.id || agenda.docId,
                cliente: cliente.nome || '',
                barbeiroId: barbeiro.id || '',
                barbeiroNome: barbeiro.nome || '',
                servico: servico.descricao || '',
                categoria: mapaCategorias[servico.id] || sugerirCategoria(servico.descricao),
                pagamento: '',
                valorBruto: numero(servico.preco),
                desconto: 0,
                status: statusPadrao || 'Recebido',
                observacao: '',
                origem: 'agenda',
                servicoId: servico.id || ''
            }
        })
        .filter((item) => item.data)

    if(!novos.length){
        return { importados: 0, itens: [] }
    }

    const lotes = []
    let lote = db.batch()
    let operacoes = 0

    novos.forEach((item) => {
        const doc = refReceitas.doc()
        lote.set(doc, { ...item, criadoEm: new Date().toISOString() })
        operacoes += 1

        if(operacoes === 450){
            lotes.push(lote)
            lote = db.batch()
            operacoes = 0
        }
    })

    if(operacoes > 0){
        lotes.push(lote)
    }

    await Promise.all(lotes.map((item) => item.commit()))

    return { importados: novos.length, itens: novos }
}
